import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { StickyHeader } from "../intro/components/landing-new";
import { MapPin, Home, Search, ArrowLeft } from "lucide-react";

export const NotFoundV2 = () => {
  const { t } = useTranslation();
  const location = useLocation();

  // Get the path that was navigated to (for direct /404 visits, there is no referred path)
  const notFoundPath = location.state?.from || "";

  // Set page title
  useEffect(() => {
    document.title = t("not_found.page_title", {
      defaultValue: "404 - Page Not Found | Sotravel",
    });

    // Restore title when leaving the page
    return () => {
      document.title = "Sotravel";
    };
  }, [t]);

  return (
    <div className="tw-min-h-screen tw-bg-gray-50">
      <StickyHeader />

      <div className="tw-container tw-mx-auto tw-px-4 tw-pt-24 tw-pb-12 tw-max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-overflow-hidden"
        >
          <div className="tw-bg-gradient-to-r tw-from-blue-500 tw-to-indigo-600 tw-px-6 tw-py-12 tw-text-center">
            <h1 className="tw-text-6xl tw-font-bold tw-text-white tw-mb-4">
              404
            </h1>
            <p className="tw-text-xl tw-text-blue-100">
              {t("not_found.page_not_exist", {
                defaultValue: "Page does not exist",
              })}
            </p>
            {notFoundPath && (
              <p className="tw-mt-2 tw-text-blue-200 tw-text-sm tw-opacity-80 tw-break-all">
                {notFoundPath}
              </p>
            )}
          </div>

          <div className="tw-px-6 tw-py-12">
            <div className="tw-flex tw-flex-col tw-items-center tw-text-center">
              <div className="tw-mb-8">
                <MapPin className="tw-w-16 tw-h-16 tw-text-gray-300" />
              </div>

              <h2 className="tw-text-2xl tw-font-bold tw-text-gray-800 tw-mb-4">
                {t("not_found.lost_your_way", {
                  defaultValue: "Looks like you've lost your way",
                })}
              </h2>

              <p className="tw-text-gray-600 tw-mb-8 tw-max-w-md">
                {t("not_found.description", {
                  defaultValue:
                    "The page you're looking for doesn't exist or has been moved. Let's get you back on track!",
                })}
              </p>

              <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4">
                <Link to="/">
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="tw-flex tw-items-center tw-justify-center tw-px-6 tw-py-3 tw-bg-blue-600 tw-text-white tw-rounded-lg tw-font-medium hover:tw-bg-blue-700 tw-transition tw-w-full sm:tw-w-auto"
                  >
                    <Home className="tw-w-4 tw-h-4 tw-mr-2" />
                    {t("not_found.go_home", { defaultValue: "Go to Homepage" })}
                  </motion.button>
                </Link>

                <Link to="/all-trips">
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="tw-flex tw-items-center tw-justify-center tw-px-6 tw-py-3 tw-bg-white tw-text-gray-700 tw-border tw-border-gray-300 tw-rounded-lg tw-font-medium hover:tw-bg-gray-50 tw-transition tw-w-full sm:tw-w-auto"
                  >
                    <Search className="tw-w-4 tw-h-4 tw-mr-2" />
                    {t("not_found.explore_trips", {
                      defaultValue: "Explore Trips",
                    })}
                  </motion.button>
                </Link>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="tw-flex tw-items-center tw-justify-center tw-px-6 tw-py-3 tw-bg-white tw-text-gray-700 tw-border tw-border-gray-300 tw-rounded-lg tw-font-medium hover:tw-bg-gray-50 tw-transition tw-w-full sm:tw-w-auto"
                  onClick={() => window.history.back()}
                >
                  <ArrowLeft className="tw-w-4 tw-h-4 tw-mr-2" />
                  {t("not_found.go_back", { defaultValue: "Go Back" })}
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
